///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// How much of the canvas the React chrome covers on each side (CSS px).
/// The UI publishes it (useViewportInsetsPublisher, from useChromeInsets)
/// and EarthActivity applies it each frame as a lens shift on the world
/// camera, so "center of the view" — fly-to targets, entity focus, the
/// crosshair — means the center of the globe the user can actually see,
/// not the center of the DOM viewport half-hidden under the data dock.
///

import type { Perspective } from "@stovokor/camera/Perspective"

export interface ViewportInsets {
	left: number
	right: number
	top: number
	bottom: number
}

let target: ViewportInsets = { left: 0, right: 0, top: 0, bottom: 0 }

// Current (eased) shift in NDC units, so an opening dock glides the globe
// over instead of snapping it
let currentX = 0
let currentY = 0
const cEaseRate = 0.18

export function setViewportInsets(insets: ViewportInsets): void {
	target = insets
}

export function getViewportInsets(): ViewportInsets {
	return target
}

/**
 * Per-frame: ease the world camera's lens shift toward the published
 * insets. Returns true while still moving (callers may skip redundant
 * work once settled).
 */
export function applyViewportInsets(projection: Perspective): boolean {
	const width = projection.width || 1
	const height = projection.height || 1
	// Visible-area center relative to the canvas center, in NDC (y up)
	const targetX = (target.left - target.right) / width
	const targetY = (target.bottom - target.top) / height

	const dx = targetX - currentX
	const dy = targetY - currentY
	if (Math.abs(dx) < 1e-4 && Math.abs(dy) < 1e-4) {
		currentX = targetX
		currentY = targetY
	} else {
		currentX += dx * cEaseRate
		currentY += dy * cEaseRate
	}

	const moving = currentX !== targetX || currentY !== targetY
	if (projection.offsetX !== currentX || projection.offsetY !== currentY) {
		projection.offsetX = currentX
		projection.offsetY = currentY
	}
	return moving
}
